'use client';

import { Checkbox } from '../../components/ui/checkbox';
import { ColorInput } from '../../components/ui/color-input';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Segmented } from '../../components/ui/segmented';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import type {
    AnyField,
    CategorySelectField,
    ColorField,
    DimensionsField as DimensionsFieldDef,
    DimensionsValue,
    EnumField,
    MarketSelectField as MarketSelectFieldDef,
    UtmValue,
    WidgetConfigState,
} from '../fields';
import type { WidgetSchema } from '../types';
import { EventSearchField } from './EventSearchField';
import { MarketSelectField } from './MarketSelectField';

interface WidgetConfigPanelProps {
    schema: WidgetSchema;
    config: WidgetConfigState;
    setField: (key: string, value: unknown) => void;
}

const THEME_OPTIONS = [
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
];

export function WidgetConfigPanel({ schema, config, setField }: WidgetConfigPanelProps) {
    const eventTicker = String(config['eventTicker'] ?? '');

    const visibleFields = schema.fields.filter(
        (field) => !field.hidden && (!field.visibleWhen || field.visibleWhen(config)),
    );

    if (visibleFields.length === 0) {
        return <p className="typ-body-x20 text-text-x20">This widget has no configuration options.</p>;
    }

    return (
        <div className="space-y-5">
            {visibleFields.map((field) => {
                const onChange = (value: unknown) => setField(field.key, value);
                const value = config[field.key];

                if (field.kind === 'boolean') {
                    return (
                        <label key={field.key} className="flex cursor-pointer items-start gap-2.5">
                            <Checkbox
                                id={field.key}
                                checked={Boolean(value)}
                                onCheckedChange={(checked) => onChange(checked)}
                                className="mt-0.5"
                            />
                            <span className="typ-body-x20 leading-snug text-text-x10">{field.label}</span>
                        </label>
                    );
                }

                return (
                    <div key={field.key} className="space-y-2">
                        <Label htmlFor={field.key} className="typ-emphasis-x10 block text-text-x10">
                            {field.label}
                        </Label>
                        {field.description && <p className="typ-body-x10 text-text-x20">{field.description}</p>}
                        <FieldControl field={field} value={value} onChange={onChange} eventTicker={eventTicker} />
                    </div>
                );
            })}
        </div>
    );
}

interface FieldControlProps {
    field: AnyField;
    value: unknown;
    onChange: (value: unknown) => void;
    eventTicker: string;
}

function FieldControl({ field, value, onChange, eventTicker }: FieldControlProps) {
    switch (field.kind) {
        case 'event-search':
            return (
                <EventSearchField
                    value={String(value ?? '')}
                    onChange={onChange}
                    allowManualTicker={field.allowManualTicker}
                />
            );

        case 'market-select':
            return (
                <MarketSelectField
                    field={field as MarketSelectFieldDef}
                    value={(value as string[]) ?? []}
                    onChange={onChange}
                    eventTicker={eventTicker}
                />
            );

        case 'theme':
            return (
                <Segmented
                    value={String(value ?? 'light')}
                    onValueChange={onChange}
                    options={THEME_OPTIONS}
                    aria-label="Theme"
                />
            );

        case 'dimensions':
            return <DimensionsControl field={field as DimensionsFieldDef} value={value} onChange={onChange} />;

        case 'category-select': {
            const options = (field as CategorySelectField).options;
            return (
                <Select value={String(value ?? '')} onValueChange={onChange}>
                    <SelectTrigger id={field.key}>
                        <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                        {options.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                                {option.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            );
        }

        case 'enum': {
            const enumField = field as EnumField;
            if (enumField.control === 'segmented') {
                return <Segmented value={String(value ?? '')} onValueChange={onChange} options={enumField.options} />;
            }
            return (
                <Select value={String(value ?? '')} onValueChange={onChange}>
                    <SelectTrigger id={field.key}>
                        <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                        {enumField.options.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                                {option.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            );
        }

        case 'color':
            return (
                <ColorInput
                    id={field.key}
                    value={String(value ?? (field as ColorField).defaultValue)}
                    onChange={onChange}
                />
            );

        case 'utm': {
            const utm = (value ?? { utmSource: '', utmCampaign: '', promoCode: '' }) as UtmValue;
            return (
                <div className="space-y-2">
                    <Input
                        value={utm.utmSource}
                        placeholder="utm source (e.g. website.com)"
                        onChange={(e) => onChange({ ...utm, utmSource: e.target.value })}
                        className="h-9"
                    />
                    <Input
                        value={utm.utmCampaign}
                        placeholder="utm campaign"
                        onChange={(e) => onChange({ ...utm, utmCampaign: e.target.value })}
                        className="h-9"
                    />
                    <Input
                        value={utm.promoCode}
                        placeholder="promo code"
                        onChange={(e) => onChange({ ...utm, promoCode: e.target.value })}
                        className="h-9"
                    />
                    <p className="typ-body-x10 text-text-x20">utm medium is always set to “widget”.</p>
                </div>
            );
        }
    }
}

/** Size control: editable custom width / height. */
function DimensionsControl({
    field,
    value,
    onChange,
}: {
    field: DimensionsFieldDef;
    value: unknown;
    onChange: (value: unknown) => void;
}) {
    const dims = (value ?? field.defaultValue ?? { width: '100%', height: '100%' }) as DimensionsValue;

    return (
        <div className="flex gap-2">
            <div className="flex-1 space-y-1">
                <Label htmlFor={`${field.key}-w`} className="typ-body-x10 text-text-x20">
                    Width
                </Label>
                <Input
                    id={`${field.key}-w`}
                    value={dims.width}
                    placeholder="100%"
                    onChange={(e) => onChange({ ...dims, width: e.target.value })}
                    className="h-9 font-mono text-xs"
                />
            </div>
            <div className="flex-1 space-y-1">
                <Label htmlFor={`${field.key}-h`} className="typ-body-x10 text-text-x20">
                    Height
                </Label>
                <Input
                    id={`${field.key}-h`}
                    value={dims.height}
                    placeholder="400px"
                    onChange={(e) => onChange({ ...dims, height: e.target.value })}
                    className="h-9 font-mono text-xs"
                />
            </div>
        </div>
    );
}
